import { ModelType } from '@smx-ui-model/types';
import { Select } from 'antd';
import React, { forwardRef, Suspense, useEffect, useRef, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';

import { DEBOUNCE_DELAY } from '../../../../shared/const';
import { DynamicListLoader, LIMIT_DYNAMIC_LIST } from './dynamic-list-loader';
import { EntitySelectOption } from './entity-select-option';
import { SuspenseLoadingStatus } from './suspense-loading-status';
import { useSelectFirstItemAfterUpdateDynamicList } from './use-select-first-item-after-update-dynamic-list';

export interface DynamicListEntityProps<T, K, M> {
    model: ModelType<T>;
    disabled?: boolean;
    listName: string;
    listFieldName: (keyof T)[];
    mode?: 'tags' | 'multiple';
    input?: K;
    style?: React.CSSProperties;
    renderLabel?: (value: any) => React.ReactNode;
    onSelect?: (value: string, data: M) => void;
    refreshTrigger?: number | string;
}

//ToDo: need add loading with cursor now pageInfo doesn't include endCursor and hasNextPage is false
export const DynamicListEntity = <T, K, M>() =>
    forwardRef<any, DynamicListEntityProps<T, K, M>>(
        (
            { model, listName, listFieldName, style, renderLabel, onSelect, input = {}, refreshTrigger, ...restProps },
            ref,
        ) => {
            const [isLoading, setIsLoading] = useState<boolean>(false);
            const [searchString, setSearchString] = useState<string>();
            const [internalInput, setInternalInput] = useState<K>(input as K);
            const [data, setData] = useState<any>();
            const timerRef = useRef<NodeJS.Timeout>(null);
            const previousSearchString = useRef<string>('');
            const environment = useRelayEnvironment();

            useSelectFirstItemAfterUpdateDynamicList(data, listName, onSelect);

            const conditionForSearchingOnServer = data?.[listName].edges.length === LIMIT_DYNAMIC_LIST;

            useEffect(() => {
                if (
                    conditionForSearchingOnServer ||
                    (searchString || '').length < previousSearchString.current.length
                ) {
                    const newInternalInput =
                        searchString && model.searchFilterField
                            ? {
                                  ...internalInput,
                                  filter: {
                                      [model.searchFilterField]: {
                                          operator: 'CONTAINS',
                                          value: searchString,
                                      },
                                  },
                              }
                            : internalInput;

                    setInternalInput(searchString ? newInternalInput : (input as K));
                }
            }, [searchString]);

            return (
                <>
                    <Suspense
                        fallback={
                            <SuspenseLoadingStatus
                                onStart={() => {
                                    setIsLoading(true);
                                }}
                                onEnd={() => {
                                    setIsLoading(false);
                                }}
                            />
                        }
                    >
                        <DynamicListLoader
                            model={model}
                            input={internalInput}
                            refreshTrigger={refreshTrigger}
                            onChanged={(data) => {
                                // without timeout get error console
                                setTimeout(() => setData(data));
                            }}
                        />
                    </Suspense>

                    <Select
                        ref={ref}
                        allowClear={false}
                        style={style}
                        onSelect={(value) => {
                            previousSearchString.current = searchString || '';
                            setSearchString('');
                            onSelect?.(value, data?.[listName]);
                        }}
                        {...restProps}
                        loading={isLoading}
                        showSearch={true}
                        filterOption={(inputValue, option) => {
                            if (!inputValue || conditionForSearchingOnServer) return true;

                            const searchLower = inputValue.toLowerCase();
                            const optionValue = option?.value as string;

                            const entityFromStore = environment?.getStore().getSource().get(optionValue);
                            const searchText = (listFieldName
                                ?.map((name) => entityFromStore?.[name as string])
                                .join(' ') || entityFromStore?.['name']) as string;

                            if (searchText) {
                                return searchText.toLowerCase().includes(searchLower);
                            }

                            return false;
                        }}
                        onSearch={(value: string) => {
                            if (timerRef.current) {
                                clearTimeout(timerRef.current);
                            }

                            timerRef.current = setTimeout(() => {
                                previousSearchString.current = searchString || '';
                                setSearchString(value);
                            }, DEBOUNCE_DELAY);
                        }}
                    >
                        {data?.[listName]?.edges.map((edge: any) => (
                            <Select.Option key={edge.node.__id} value={edge.node.__id}>
                                {renderLabel ? (
                                    renderLabel(edge.node)
                                ) : (
                                    <EntitySelectOption node={edge.node} listFieldName={listFieldName} />
                                )}
                            </Select.Option>
                        ))}
                    </Select>
                </>
            );
        },
    );
